<!--
AssistantHistoryPanel — on-demand overlay panel listing the user's chat threads.

Presentational: props in (chats, activeChatId, loading, archivedView), events out
(create / select / rename / delete / archive / toggleArchived / close).

Identical event contract to AssistantChatList so the sidebar wires it the same way
AssistantModal.vue wires the list. The rename logic (startRename / commitRename /
cancelRename) is ported verbatim from AssistantChatList.vue:157-175, including the
blank-AND-unchanged skip guard at line 166 (parity checklist row #11).

Delete is emitted as `delete` — the parent owns the confirm dialog, exactly as
AssistantModal.handleDelete does (lines 181-200 in AssistantModal.vue).
-->
<template>
  <div class="assistant-history-panel" data-testid="assistant-history-panel">
    <!-- Header row -->
    <div class="assistant-history-header">
      <span class="assistant-history-title">{{ t('Conversas') }}</span>
      <Button
        data-testid="assistant-history-close"
        icon="pi pi-times"
        severity="secondary"
        text
        rounded
        size="small"
        :aria-label="t('Fechar histórico')"
        @click="emit('close')"
      />
    </div>

    <!-- Ativas / Arquivadas segmented control -->
    <select-button
      class="assistant-history-filter"
      data-testid="assistant-history-filter"
      :model-value="archivedView ? 'archived' : 'active'"
      :options="filterOptions"
      option-label="label"
      option-value="value"
      :allow-empty="false"
      @update:model-value="onFilterChange"
    />

    <!-- New chat button -->
    <Button
      class="assistant-history-new-chat"
      data-testid="assistant-new-chat"
      :label="t('Nova conversa')"
      icon="pi pi-plus"
      severity="primary"
      :disabled="loading"
      @click="emit('create')"
    />

    <!-- Empty state -->
    <p
      v-if="chats.length === 0"
      class="assistant-chat-empty"
      data-testid="assistant-chat-empty"
    >
      {{ t('Nenhuma conversa ainda') }}
    </p>

    <!-- Chat list -->
    <ul v-else class="assistant-history-items">
      <li
        v-for="(chat, index) in chats"
        :key="chat.id"
        class="assistant-history-row"
        :class="{ 'assistant-history-row--active': chat.id === activeChatId }"
        :data-testid="`assistant-history-item-${chat.id}`"
        :data-testid-index="index"
        :data-testid-chat-id="chat.id"
      >
        <!-- Inline rename mode -->
        <template v-if="editingId === chat.id">
          <input-text
            class="assistant-chat-rename-input"
            data-testid="assistant-chat-rename-input"
            :elem-id="`assistant_rename_${chat.id}`"
            :fluid="true"
            v-model="draftTitle"
            @keyup.enter="commitRename(chat.id)"
            @keyup.esc="cancelRename"
          />
          <Button
            data-testid="assistant-rename-confirm"
            icon="pi pi-check"
            severity="success"
            text
            rounded
            size="small"
            :aria-label="t('Salvar nome')"
            :disabled="loading || draftTitle.trim().length === 0"
            @click="commitRename(chat.id)"
          />
          <Button
            data-testid="assistant-rename-cancel"
            icon="pi pi-times"
            severity="secondary"
            text
            rounded
            size="small"
            :aria-label="t('Cancelar')"
            @click="cancelRename"
          />
        </template>

        <!-- Normal display row -->
        <template v-else>
          <button
            type="button"
            class="assistant-history-row-select"
            data-testid="assistant-chat-item-select"
            :disabled="loading"
            @click="emit('select', chat.id)"
          >
            <span class="assistant-history-row-title">{{ chat.title }}</span>
            <span class="assistant-history-row-date">{{ relativeDate(chat.updatedAt) }}</span>
          </button>

          <!-- Kebab menu via DropdownMenu wrapper (src/ui/DropdownMenu.vue) -->
          <dropdown-menu
            data-testid="assistant-history-kebab"
            button-text=""
            :items="kebabItemsFor(chat)"
            @select="(item: KebabItem) => handleKebabSelect(chat, item)"
          />
        </template>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ChatSummary } from '../../composables/aiAgentContract';
import DropdownMenu from '../../ui/DropdownMenu.vue';
import SelectButton from '../../ui/SelectButton.vue';

const props = defineProps<{
  chats: ChatSummary[];
  activeChatId: number | null;
  loading: boolean;
  archivedView: boolean;
}>();

const emit = defineEmits<{
  create: [];
  select: [chatId: number];
  rename: [chatId: number, title: string];
  delete: [chatId: number];
  archive: [chatId: number];
  toggleArchived: [];
  close: [];
}>();

const { t } = useI18n();

// ── Filter options ──────────────────────────────────────────────────────────

const filterOptions = computed(() => [
  { label: t('Ativas'), value: 'active' },
  { label: t('Arquivadas'), value: 'archived' },
]);

function onFilterChange(value: string): void {
  // Only emit toggleArchived when value actually changes.
  const currentValue = props.archivedView ? 'archived' : 'active';
  if (value !== currentValue) {
    emit('toggleArchived');
  }
}

// ── Inline rename (ported verbatim from AssistantChatList.vue:157-175) ──────

const editingId = ref<number | null>(null);
const draftTitle = ref<string>('');

function startRename(chat: ChatSummary): void {
  editingId.value = chat.id;
  draftTitle.value = chat.title;
}

function commitRename(chatId: number): void {
  const title = draftTitle.value.trim();
  const current = props.chats.find((c) => c.id === chatId);
  // Skip the request when blank or unchanged.
  if (title.length > 0 && title !== current?.title) {
    emit('rename', chatId, title);
  }
  cancelRename();
}

function cancelRename(): void {
  editingId.value = null;
  draftTitle.value = '';
}

// ── Kebab menu actions ───────────────────────────────────────────────────────

type KebabAction = 'rename' | 'archive' | 'delete';

interface KebabItem {
  label: string;
  value: KebabAction;
}

function kebabItemsFor(_chat: ChatSummary): KebabItem[] {
  return [
    { label: t('Renomear'), value: 'rename' },
    { label: t('Arquivar'), value: 'archive' },
    { label: t('Excluir'), value: 'delete' },
  ];
}

function handleKebabSelect(chat: ChatSummary, item: KebabItem): void {
  if (item.value === 'rename') {
    startRename(chat);
  } else if (item.value === 'archive') {
    emit('archive', chat.id);
  } else if (item.value === 'delete') {
    emit('delete', chat.id);
  }
}

// ── Relative date helper ─────────────────────────────────────────────────────

// Counts are CONCATENATED, never `{n}` placeholders — the API-driven catalog
// returns missing keys as raw uncompiled strings, so vue-i18n interpolation
// renders the literal "{n}". Concatenation is the codebase convention
// (see CompromissoTable's "Carregados N registros" pattern).
function relativeDate(iso: string): string {
  const then = new Date(iso);
  const now = Date.now();
  const diffMs = now - then.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return t('agora');
  if (diffMins < 60) return `${diffMins} ${t('min atrás')}`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ${t('h atrás')}`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return t('ontem');
  if (diffDays < 30) return `${diffDays} ${t('dias atrás')}`;
  return then.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
</script>

<style scoped>
.assistant-history-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--p-surface-0);
  height: 100%;
  overflow-y: auto;
}

.assistant-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.assistant-history-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.assistant-history-filter {
  width: 100%;
}

:deep(.assistant-history-filter .p-selectbutton) {
  width: 100%;
  display: flex;
}

:deep(.assistant-history-filter .p-togglebutton) {
  flex: 1;
  justify-content: center;
}

.assistant-history-new-chat {
  width: 100%;
}

.assistant-chat-empty {
  color: var(--p-text-muted-color);
  text-align: center;
  font-size: 0.85rem;
  margin: 0.5rem 0;
}

.assistant-history-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  flex: 1;
}

.assistant-history-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  padding: 0.125rem 0.25rem;
  transition: background 0.15s;
}

.assistant-history-row:hover {
  background: var(--p-surface-100);
}

.assistant-history-row--active {
  background: var(--p-surface-200);
}

.assistant-history-row-select {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  background: none;
  border: none;
  padding: 0.375rem 0.5rem;
  cursor: pointer;
  text-align: left;
  color: var(--p-text-color);
  font: inherit;
  min-width: 0;
}

.assistant-history-row-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  font-size: 0.875rem;
}

.assistant-history-row-date {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
}
</style>
